import type { AgencyTaskStatus, AgencyTaskUrgency } from '@tsr/shared-types';

/**
 * Shared agency-task status badge palette (#379) — the single source of truth for the
 * OUTBOUND task status chip, consumed by OutboundTasksPanel + AgencyTaskDetailBody (mirrors
 * droneMissionUi.ts's MISSION_STATUS_BADGE so the palette is defined once, not per-surface).
 * Keyed by AgencyTaskStatus so the map is exhaustive at compile time; 3b-2b adds no new
 * status, only wires the target-side transitions into these existing ones.
 */
export interface AgencyTaskStatusBadge {
  cls: string;
  label: string;
}

export const AGENCY_TASK_STATUS_BADGE: Record<AgencyTaskStatus, AgencyTaskStatusBadge> = {
  issued: { cls: 'bg-amber-600/80 text-white', label: 'Issued' },
  acknowledged: { cls: 'bg-sky-600/80 text-white', label: 'Acknowledged' },
  accepted: { cls: 'bg-cyan-600/80 text-white', label: 'Accepted' },
  in_progress: { cls: 'bg-indigo-600/80 text-white', label: 'In progress' },
  completed: { cls: 'bg-emerald-700/80 text-white', label: 'Completed' },
  declined: { cls: 'bg-rose-700/80 text-white', label: 'Declined' },
  cancelled: { cls: 'bg-slate-600/80 text-slate-200', label: 'Cancelled' },
};

/** Badge for an agency-task status string, with a safe fallback for an unexpected value. */
export function agencyTaskStatusBadge(status: string): AgencyTaskStatusBadge {
  return (
    AGENCY_TASK_STATUS_BADGE[status as AgencyTaskStatus] ?? {
      cls: 'bg-slate-600/80 text-slate-200',
      label: status,
    }
  );
}

/**
 * Humanized agency-task urgency label (W4a / council B) — an outbound row must never
 * render the raw `routine`/`urgent`/`immediate` column value. `AgencyTaskView.urgency`
 * is free-text on the wire, so `agencyTaskUrgencyLabel` maps the known vocabulary and
 * Title-cases any unexpected value rather than leaking a bare lowercased enum.
 */
export const AGENCY_TASK_URGENCY_LABEL: Record<AgencyTaskUrgency, string> = {
  routine: 'Routine',
  urgent: 'Urgent',
  immediate: 'Immediate',
};

export function agencyTaskUrgencyLabel(urgency: string): string {
  return (
    AGENCY_TASK_URGENCY_LABEL[urgency as AgencyTaskUrgency] ??
    (urgency ? urgency.charAt(0).toUpperCase() + urgency.slice(1) : urgency)
  );
}
